import React, { useEffect, useState } from "react";
import { useParams, useLocation, Link } from "react-router-dom";
import { api } from "@/lib/api";
import { Trophy, X, CheckCircle2, ArrowRight, Sparkles, BookOpen } from "lucide-react";
import { toast } from "sonner";

export default function PracticeResults() {
  const { slug, attemptId } = useParams();
  const { state } = useLocation();
  const [data, setData] = useState(state || null);
  const [openExplain, setOpenExplain] = useState({});

  useEffect(() => {
    if (data) return;
    api.get(`/api/practice/attempts/${attemptId}`)
      .then(({ data }) => setData(data))
      .catch(() => toast.error("Could not load result"));
  }, [attemptId, data]);

  const explain = async (qid) => {
    if (openExplain[qid]) {
      setOpenExplain((o) => ({ ...o, [qid]: null }));
      return;
    }
    setOpenExplain((o) => ({ ...o, [qid]: "loading" }));
    try {
      const { data } = await api.post(`/api/practice/questions/${qid}/explain`);
      setOpenExplain((o) => ({ ...o, [qid]: data.explanation || "No explanation available" }));
    } catch (e) {
      setOpenExplain((o) => ({ ...o, [qid]: "AI explanation temporarily unavailable" }));
    }
  };

  if (!data) return <div className="p-12 text-center text-[#4B5563]">Loading…</div>;

  const passed = data.passed;
  const pct = data.score_pct ?? 0;

  return (
    <div className="max-w-4xl mx-auto px-6 py-8" data-testid="practice-results">
      {/* HERO */}
      <div className={`rounded-3xl p-8 text-white ${passed ? "bg-[#064E3B]" : "bg-[#D92D20]"}`}>
        <div className="flex items-start justify-between flex-wrap gap-4">
          <div>
            <div className="text-xs uppercase tracking-widest font-bold opacity-80">{passed ? "Passed!" : "Keep practicing"}</div>
            <div className="font-display font-black text-4xl mt-1" data-testid="results-score-pct">{pct}%</div>
            <div className="text-sm opacity-80 mt-1">
              {data.score}/{data.total_questions} correct · {Math.floor((data.time_taken_sec || 0)/60)}m {(data.time_taken_sec || 0) % 60}s
            </div>
            <div className="text-xs opacity-70 mt-1">Passing score: {data.passing_score}%</div>
          </div>
          <div className="text-7xl">{passed ? <Trophy className="w-20 h-20 opacity-90" /> : <BookOpen className="w-20 h-20 opacity-90" />}</div>
        </div>
      </div>

      {/* CATEGORY BREAKDOWN */}
      <div className="mt-6 bg-white border border-[#E5E7EB] rounded-2xl p-6">
        <div className="font-display font-bold text-lg">Category breakdown</div>
        <div className="mt-4 space-y-2">
          {Object.entries(data.category_breakdown || {}).map(([cat, s]) => (
            <div key={cat} data-testid={`cat-${cat}`}>
              <div className="flex justify-between text-sm">
                <span>{cat}</span>
                <b className={s.pct >= 80 ? "text-[#064E3B]" : s.pct >= 60 ? "text-[#92400E]" : "text-[#D92D20]"}>
                  {s.correct}/{s.total} · {s.pct}%
                </b>
              </div>
              <div className="h-2 bg-[#F3F4F6] rounded-full mt-1 overflow-hidden">
                <div className="h-full transition-all"
                  style={{ width: `${s.pct}%`, background: s.pct >= 80 ? "#064E3B" : s.pct >= 60 ? "#F59E0B" : "#D92D20" }} />
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* CTAs */}
      <div className="mt-6 flex gap-3 flex-wrap">
        <Link to={`/practice/${slug}`} data-testid="results-retry" className="btn-primary inline-flex items-center gap-2">
          Practice again <ArrowRight className="w-4 h-4" />
        </Link>
        <Link to="/practice/dashboard" data-testid="results-dashboard" className="btn-outline">
          View progress
        </Link>
        <Link to="/tutor-landing" data-testid="results-tutoring"
          className="text-sm px-5 py-3 rounded-full border border-[#064E3B] text-[#064E3B] font-semibold hover:bg-[#D1FAE5] inline-flex items-center gap-1">
          <Sparkles className="w-4 h-4" /> Get AI Tutoring help (free)
        </Link>
      </div>

      {/* REVIEW */}
      <div className="mt-8">
        <div className="font-display font-bold text-2xl">Question review</div>
        <div className="mt-4 space-y-4">
          {(data.review || []).map((r, i) => (
            <div key={r.id} data-testid={`review-q-${i}`}
              className={`bg-white border-l-4 ${r.is_correct ? "border-[#064E3B]" : "border-[#D92D20]"} border-r border-y border-[#E5E7EB] rounded-r-2xl p-5`}>
              <div className="flex items-start justify-between gap-3">
                <div className="text-xs uppercase tracking-widest font-bold" style={{ color: r.is_correct ? "#064E3B" : "#D92D20" }}>
                  Q{i + 1} · {r.category} · {r.is_correct ? "Correct" : "Incorrect"}
                </div>
                {r.is_correct ? <CheckCircle2 className="w-5 h-5 text-[#064E3B]" /> : <X className="w-5 h-5 text-[#D92D20]" />}
              </div>
              <div className="font-display font-bold mt-2">{r.question}</div>
              <div className="mt-3 space-y-2">
                {r.options.map((o, oi) => {
                  const isCorrect = oi === r.correct_index;
                  const isSelected = oi === r.selected;
                  let cls = "border-[#E5E7EB]";
                  if (isCorrect) cls = "border-2 border-[#064E3B] bg-[#D1FAE5] font-semibold";
                  else if (isSelected) cls = "border-2 border-[#D92D20] bg-[#FEE2E2] font-semibold";
                  return (
                    <div key={`${r.id}-${oi}`} className={`text-sm border rounded-lg px-3 py-2 flex items-center justify-between gap-2 ${cls}`}>
                      <span>
                        <span className="font-bold mr-2">{String.fromCharCode(65 + oi)}.</span> {o}
                      </span>
                      <span className="flex items-center gap-1 flex-shrink-0">
                        {isCorrect && (
                          <span className="inline-flex items-center gap-1 text-[10px] font-bold text-[#064E3B] uppercase tracking-widest">
                            <CheckCircle2 className="w-4 h-4" /> Correct answer
                          </span>
                        )}
                        {isSelected && !isCorrect && (
                          <span className="inline-flex items-center gap-1 text-[10px] font-bold text-[#D92D20] uppercase tracking-widest">
                            <X className="w-4 h-4" /> Your answer
                          </span>
                        )}
                      </span>
                    </div>
                  );
                })}
              </div>
              <p className="mt-3 text-sm text-[#4B5563]"><b>Explanation:</b> {r.explanation}</p>
              <button data-testid={`explain-${i}`} onClick={() => explain(r.id)}
                className="mt-3 text-xs font-semibold text-[#D92D20] inline-flex items-center gap-1">
                <Sparkles className="w-3 h-3" /> {openExplain[r.id] ? "Hide AI explanation" : "Explain This Question (AI)"}
              </button>
              {openExplain[r.id] && openExplain[r.id] !== "loading" && (
                <div className="mt-3 bg-[#FEF2F2] border border-[#FECACA] rounded-xl p-3 text-sm whitespace-pre-wrap" data-testid={`explain-text-${i}`}>
                  {openExplain[r.id]}
                </div>
              )}
              {openExplain[r.id] === "loading" && (
                <div className="mt-3 text-xs text-[#4B5563] italic">AI thinking…</div>
              )}
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}
